import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Receipt, Eye, EyeOff, FileText, X } from "lucide-react";
import { TermsBody, TERMS_VERSION } from "./TermsPage";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("يرجى تعبئة جميع الحقول المطلوبة");
      return;
    }
    if (password.length < 6) {
      toast.error("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    if (!accepted) {
      toast.error("يجب الموافقة على اتفاقية الاستخدام للمتابعة");
      return;
    }
    setLoading(true);
    try {
      const result = await register(email, password, name, {
        terms_accepted: true,
        terms_version: TERMS_VERSION,
      });
      if (result?.pending) {
        toast.success("تم إنشاء الحساب بنجاح — بانتظار موافقة المسؤول");
        navigate("/login", {
          replace: true,
          state: { notice: result.message },
        });
      } else {
        toast.success("تم إنشاء الحساب بنجاح");
        navigate("/");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل إنشاء الحساب");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#F5F4EF]" dir="rtl">
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1778222239168-c61866c8e3a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHBhcGVyJTIwdGV4dHVyZSUyMHRlcnJhY290dGElMjBzYW5kfGVufDB8fHx8MTc3ODUyNDA4OHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F3A33]/90 via-[#1F3A33]/55 to-[#1F3A33]/15" />
        <div className="absolute inset-0 brand-stripes opacity-60" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white text-[#1F3A33] flex items-center justify-center shadow-lg">
              <Receipt className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-readex font-semibold text-2xl leading-none">فواتيري</div>
              <div className="text-xs text-white/70 mt-1 tracking-wider uppercase">Fawateery</div>
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-xs mb-6 font-readex">
              <span className="w-1.5 h-1.5 rounded-full bg-[#A3C4B0] soft-pulse" />
              مجاناً تماماً
            </div>
            <h1 className="font-readex font-medium text-4xl xl:text-5xl leading-tight mb-4">
              ابدأ بإدارة فواتيرك<br />
              <span className="text-[#A3C4B0]">في أقل من دقيقة</span>
            </h1>
            <p className="text-white/85 text-base leading-relaxed max-w-md">
              إنشاء حساب مجاني، إضافة فواتيرك، وحفظ صورها مع تقارير شهرية احترافية.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-md bg-[#1F3A33] text-white flex items-center justify-center">
              <Receipt className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <span className="font-readex font-semibold text-xl text-[#1A1C19]">فواتيري</span>
          </div>

          <div className="mb-8">
            <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-3 font-readex">انضم إلينا</div>
            <h2 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">إنشاء حساب جديد</h2>
            <p className="text-[#5C605A] mt-2">سجل بياناتك للبدء فوراً.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="block text-sm font-medium text-[#1A1C19] mb-2 font-readex">الاسم</label>
              <input
                type="text"
                data-testid="register-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border border-[#1A1C19]/15 rounded-md px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#1F3A33]/15 focus:border-[#1F3A33] transition-all"
                placeholder="اسمك الكامل"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1C19] mb-2 font-readex">البريد الإلكتروني</label>
              <input
                type="email"
                data-testid="register-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#1A1C19]/15 rounded-md px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#1F3A33]/15 focus:border-[#1F3A33] transition-all"
                placeholder="name@example.com"
                dir="ltr"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1C19] mb-2 font-readex">كلمة المرور</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  data-testid="register-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#1A1C19]/15 rounded-md px-4 py-3 pe-12 text-base focus:outline-none focus:ring-2 focus:ring-[#1F3A33]/15 focus:border-[#1F3A33] transition-all"
                  placeholder="6 أحرف على الأقل"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-[#8A8D86] hover:text-[#1A1C19]"
                  data-testid="register-show-password"
                >
                  {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="pt-2 border-t border-[#1A1C19]/8">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  data-testid="terms-accept-checkbox"
                  className="mt-1 w-5 h-5 rounded border-[#1A1C19]/30 text-[#1F3A33] focus:ring-2 focus:ring-[#1F3A33]/20 cursor-pointer accent-[#1F3A33] flex-shrink-0"
                />
                <span className="text-sm text-[#1A1C19] font-readex leading-relaxed">
                  أقرّ بأنني قرأت ووافقت على{" "}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                    data-testid="open-terms-btn"
                    className="text-[#1F3A33] underline font-medium hover:text-[#172A25]"
                  >
                    اتفاقية الاستخدام وسياسة الخصوصية
                  </button>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !accepted}
              data-testid="register-submit-btn"
              className="w-full bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 disabled:cursor-not-allowed transition-colors rounded-md px-6 py-3.5 font-medium font-readex text-base"
            >
              {loading ? "جاري الإنشاء…" : "إنشاء الحساب"}
            </button>

            <div className="text-center text-sm text-[#5C605A] pt-4">
              لديك حساب بالفعل؟{" "}
              <Link to="/login" className="text-[#1F3A33] font-medium hover:underline" data-testid="link-login">
                سجل الدخول
              </Link>
            </div>
          </form>
        </div>
      </div>

      {showTerms && (
        <div
          className="fixed inset-0 bg-black/55 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          dir="rtl"
          data-testid="terms-modal"
          onClick={() => setShowTerms(false)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-3xl max-h-[88vh] flex flex-col shadow-2xl border border-[#1A1C19]/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A1C19]/10 bg-[#FAF9F5] rounded-t-lg">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1F3A33]" />
                <h3 className="font-readex font-semibold text-lg text-[#1A1C19]">اتفاقية الاستخدام وسياسة الخصوصية</h3>
              </div>
              <button onClick={() => setShowTerms(false)} className="text-[#5C605A] hover:text-[#1A1C19]" aria-label="إغلاق" data-testid="close-terms-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="terms-modal-body">
              <TermsBody compact />
            </div>
            <div className="border-t border-[#1A1C19]/10 px-6 py-4 bg-[#FAF9F5] rounded-b-lg flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="px-4 py-2 rounded-md text-sm text-[#5C605A] hover:bg-[#1A1C19]/5 font-readex"
                data-testid="terms-close-btn"
              >
                إغلاق
              </button>
              <button
                type="button"
                onClick={() => { setAccepted(true); setShowTerms(false); }}
                data-testid="terms-accept-btn"
                className="px-5 py-2 rounded-md text-sm bg-[#1F3A33] text-white hover:bg-[#172A25] font-readex font-medium"
              >
                أوافق على الاتفاقية
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
